<template>
    <div>
        <v-text-field
            v-if="!loaded"
            :density="dense ? 'compact' : 'comfortable'"
            :class="textFieldClass"
            :label="label || $t('check_in')"
            readonly
            :single-line="singleLine"
            variant="outlined"
            hide-details
            :bg-color="backgroundColor"
            :rounded="rounded"
            :prepend-inner-icon="showIcon ? prependInnerIcon : null"
            :append-icon="appendIcon"
            :rules="rules"
            data-cy="date-rage-input"
            @click:append="clickAppend"
        />
        <client-only>
            <!-- eslint-disable vue/no-custom-modifiers-on-v-model -->
            <vc-date-picker
                v-model.range.string="range"
                :mode="mode"
                :is24hr="mode === 'dateTime'"
                :masks="masks"
                :locale="$i18n.locale"
                :min-date="minStartDate"
                :max-date="maxEndDate"
                :columns="$breakpoint.smAndDown ? 1 : 2"
                :step="1"
                :rows="1"
                :first-day-of-week="$conf.public.firstDayOfWeek || undefined"
                :popover="popover"
                data-cy="date-picker-input"
                @dayclick="dayclick"
                @popover-will-show="popoverWillShow"
            >
                <template v-slot="{inputEvents}">
                    <div v-on="inputEvents.start">
                        <v-text-field
                            :density="dense ? 'compact' : 'comfortable'"
                            :class="textFieldClass"
                            :label="label || $t('check_in')"
                            readonly
                            :single-line="singleLine"
                            variant="outlined"
                            hide-details
                            :rules="rules"
                            :bg-color="backgroundColor"
                            :rounded="rounded"
                            :prepend-inner-icon="showIcon ? prependInnerIcon : null"
                            :append-icon="appendIcon"
                            :model-value="formattedDate"
                            @click:append="clickAppend"
                        />
                    </div>
                </template>
            </vc-date-picker>
        </client-only>
    </div>
</template>

<script lang="ts">
    import {Component, Emit, Prop, Vue} from 'vue-facing-decorator'

    @Component
    export class DaterangeM extends Vue {
        @Prop({default: false, type: Boolean}) outlined
        @Prop() label
        @Prop({default: false, type: Boolean}) dense
        @Prop({default: true}) singleLine
        @Prop({default: true}) showIcon
        @Prop({default: () => ({placement: 'bottom', visibility: 'click'})}) popover
        @Prop({default: 'hotels'}) product
        @Prop({default: 'date'}) mode
        @Prop() appendIcon
        @Prop({default: 180}) maxNights
        @Prop({default: 'mdi-calendar'}) prependInnerIcon
        @Prop({default: 'white'}) backgroundColor
        @Prop({default: false, type: Boolean}) rounded
        @Prop({default: false, type: Boolean}) disableMinDate
        @Prop({default: false, type: Boolean}) disableMaxDate
        @Prop() rules

        @Prop() _start
        @Prop() _end

        selectedStartDate = null
        loaded = false

        @Emit()
        changeStart(val) {
            return val
        }

        @Emit()
        changeEnd(val) {
            return val
        }

        @Emit('click:append')
        clickAppend(event) {
            return event
        }

        mounted() {
            this.loaded = true
        }

        addDays(date, days) {
            return this.$dateFns.format(this.$dateFns.addDays(date, days))
        }

        dayclick(day) {
            if (!this.selectedStartDate) {
                this.selectedStartDate = this.$dateFns.parseISO(day.id)
            }
        }

        popoverWillShow() {
            this.selectedStartDate = null
        }

        get range() {
            return {
                start: this.start,
                end: this.end,
            }
        }

        set range(val) {
            if (!val) return
            this.start = val.start
            this.end = val.end
        }

        get minStartDate() {
            if (this.disableMinDate) {
                return null
            }

            if (this.selectedStartDate) {
                const minStartDate = this.$dateFns.subDays(this.selectedStartDate, this.maxNights)
                if (this.$dateFns.isAfter(minStartDate, new Date())) {
                    return this.$dateFns.format(minStartDate)
                }
            }
            return this.$dateFns.format(new Date())
        }

        get maxEndDate() {
            if (!this.selectedStartDate || this.disableMaxDate) return null
            return this.addDays(this.selectedStartDate, this.maxNights)
        }

        get formattedDate() {
            return this.range.start !== null && this.range.end !== null
                ? `${this.$filters.dateFormat(this.range.start)} -  ${this.$filters.dateFormat(this.range.end)}`
                : ''
        }

        get textFieldClass() {
            let className = ''
            if (this.$breakpoint.smAndDown) {
                className += 'caption'
            }
            className += ' date-range-input ' + this.product
            return className
        }

        get masks() {
            if (this.mode === 'dateTime') {
                return {modelValue: 'YYYY-MM-DDTHH:mm'}
            } else {
                return {modelValue: 'YYYY-MM-DD'}
            }
        }

        get start() {
            return this._start
        }

        set start(val) {
            this.changeStart(val)
        }

        get end() {
            return this._end
        }

        set end(val) {
            this.changeEnd(val)
        }
    }

    export default DaterangeM
</script>
